import React from 'react';
import { Area, AreaChart, CartesianGrid, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Users, MousePointerClick, Clock, ArrowUpRight, Lock } from "lucide-react";
import { Button } from "./ui/button";
import { useSubscription } from "../contexts/SubscriptionContext";

const Analytics = ({ project, supabase, onUpgrade }) => {
    const { getPlanLimits, subscription } = useSubscription();
    const limits = getPlanLimits();
    // Analytics is available for Starter and Growth (retention > 7 days or unlimited)
    const hasAccess = limits.analyticsRetentionDays === Infinity || subscription?.plan_type !== 'free';

    const [stats, setStats] = React.useState({
        totalUsers: 0,
        completionRate: 0,
        avgTime: 0,
        interactions: 0,
        chartData: []
    });
    const [loading, setLoading] = React.useState(true);
    const [selectedVariant, setSelectedVariant] = React.useState('all'); // 'all', 'control', or variant ID

    React.useEffect(() => {
        if (project?.id) {
            fetchAnalytics();
        }
    }, [project?.id, selectedVariant]);

    const fetchAnalytics = async () => {
        try {
            setLoading(true);

            // 1. Get all events for this project
            const { data: events, error } = await supabase
                .from('analytics_events')
                .select('*')
                .eq('project_id', project.id)
                .order('created_at', { ascending: true });

            if (error) throw error;

            if (!events || events.length === 0) {
                setStats({
                    totalUsers: 0,
                    completionRate: 0,
                    avgTime: 0,
                    interactions: 0,
                    chartData: [],
                    variantStats: {}
                });
                return;
            }

            // Filter events by selected variant
            const filteredEvents = selectedVariant === 'all'
                ? events
                : events.filter(e => (e.variant_id || 'control') === selectedVariant);

            // 2. Process data
            // Count unique users (unique session IDs)
            const viewEvents = filteredEvents.filter(e => e.event_type === 'view');
            const uniqueSessionIds = new Set(viewEvents.map(e => e.session_id).filter(Boolean));
            const totalUsers = uniqueSessionIds.size;

            const completions = filteredEvents.filter(e => e.event_type === 'completion').length;
            const interactions = filteredEvents.filter(e => e.event_type === 'interaction' || e.event_type === 'input').length;

            // Calculate completion rate (completions / unique users)
            const completionRate = totalUsers > 0 ? Math.round((completions / totalUsers) * 100) : 0;

            // Calculate average time (simplified)
            const avgTime = 0;

            // 3. Process Variant Stats (always show all variants for comparison)
            const variantStats = {};
            events.forEach(e => {
                const vid = e.variant_id || 'control';
                if (!variantStats[vid]) {
                    variantStats[vid] = { views: 0, completions: 0, interactions: 0 };
                }
                if (e.event_type === 'view') variantStats[vid].views++;
                if (e.event_type === 'completion') variantStats[vid].completions++;
                if (e.event_type === 'interaction' || e.event_type === 'input') variantStats[vid].interactions++;
            });

            Object.keys(variantStats).forEach(vid => {
                const s = variantStats[vid];
                s.conversionRate = s.views > 0 ? Math.round((s.completions / s.views) * 100) : 0;
            });

            // 4. Prepare chart data (grouped by day) - use filtered events
            const chartDataMap = {};

            filteredEvents.forEach(event => {
                const date = new Date(event.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                if (!chartDataMap[date]) {
                    chartDataMap[date] = { date, views: 0, completions: 0 };
                }
                if (event.event_type === 'view') chartDataMap[date].views++;
                if (event.event_type === 'completion') chartDataMap[date].completions++;
            });

            const chartData = Object.values(chartDataMap);

            // 5. Process Funnel Data
            const screenViews = {};
            filteredEvents.forEach(e => {
                if (e.event_type === 'view') {
                    const screenIndex = e.properties?.screen_index ?? 0;
                    const screenName = e.properties?.screen_name || `Screen ${screenIndex + 1}`;

                    if (!screenViews[screenIndex]) {
                        screenViews[screenIndex] = {
                            name: screenName,
                            views: 0,
                            index: screenIndex
                        };
                    }
                    screenViews[screenIndex].views++;
                }
            });

            const funnelData = Object.values(screenViews)
                .sort((a, b) => a.index - b.index)
                .map((step, index, array) => {
                    const prevStep = array[index - 1];
                    const dropOff = prevStep ? Math.round(((prevStep.views - step.views) / prevStep.views) * 100) : 0;
                    const retention = prevStep ? Math.round((step.views / prevStep.views) * 100) : 100;
                    return {
                        ...step,
                        dropOff,
                        retention
                    };
                });

            setStats({
                totalUsers,
                completionRate,
                avgTime,
                interactions,
                chartData,
                variantStats,
                funnelData
            });

        } catch (error) {
            console.error('Error fetching analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="p-8 flex items-center justify-center text-muted-foreground">Loading analytics...</div>;
    }

    return (
        <div className="relative min-h-[600px] w-full">
            {!hasAccess && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-background/50 backdrop-blur-[2px]">
                    <div className="text-center p-8 bg-background border border-border rounded-xl shadow-lg max-w-md mx-4">
                        <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Lock className="h-6 w-6 text-primary" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">Unlock Advanced Analytics</h3>
                        <p className="text-muted-foreground mb-6">
                            Upgrade to the Starter plan to see detailed insights, completion rates, and user activity logs.
                        </p>
                        <Button onClick={onUpgrade} className="w-full">
                            Upgrade Now
                        </Button>
                    </div>
                </div>
            )}

            <div className={`p-8 space-y-8 max-w-7xl mx-auto w-full ${!hasAccess ? 'filter blur-sm pointer-events-none select-none opacity-50' : ''}`}>
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-3xl font-bold tracking-tight">Analytics</h2>
                        <p className="text-muted-foreground">
                            Overview of your onboarding performance
                        </p>
                    </div>
                    <div className="flex items-center space-x-2">
                        {/* Variant Filter */}
                        {stats.variantStats && Object.keys(stats.variantStats).length > 1 && (
                            <select
                                value={selectedVariant}
                                onChange={(e) => setSelectedVariant(e.target.value)}
                                className="bg-muted px-3 py-1 rounded-md text-sm font-medium border-0 cursor-pointer hover:bg-muted/80 transition-colors"
                            >
                                <option value="all">All Variants</option>
                                <option value="control">Control</option>
                                {Object.keys(stats.variantStats)
                                    .filter(vid => vid !== 'control')
                                    .map(vid => (
                                        <option key={vid} value={vid}>
                                            {stats.variantStats[vid].name || `Variant ${vid.slice(0, 8)}`}
                                        </option>
                                    ))
                                }
                            </select>
                        )}
                        <div className="bg-muted px-3 py-1 rounded-md text-sm font-medium">
                            All Time
                        </div>
                    </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">
                                Total Users
                            </CardTitle>
                            <Users className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{stats.totalUsers}</div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">
                                Completion Rate
                            </CardTitle>
                            <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{stats.completionRate}%</div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">
                                Avg. Time Spent
                            </CardTitle>
                            <Clock className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{stats.avgTime}s</div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">
                                Interactions
                            </CardTitle>
                            <MousePointerClick className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{stats.interactions}</div>
                        </CardContent>
                    </Card>
                </div>

                {/* A/B Test Results */}
                {stats.variantStats && Object.keys(stats.variantStats).length > 1 && (
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold">A/B Test Results</h3>
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {Object.entries(stats.variantStats).map(([vid, s]) => (
                                <Card key={vid}>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm font-medium text-muted-foreground uppercase">
                                            {vid === 'control' ? 'Control' : `Variant ${vid}`}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-2">
                                            <div className="flex justify-between">
                                                <span className="text-sm text-muted-foreground">Views</span>
                                                <span className="font-bold">{s.views}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-sm text-muted-foreground">Completions</span>
                                                <span className="font-bold">{s.completions}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-sm text-muted-foreground">Conversion</span>
                                                <span className={`font-bold ${s.conversionRate > 0 ? 'text-green-600' : ''}`}>
                                                    {s.conversionRate}%
                                                </span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>
                )}

                {/* Funnel Analysis */}
                {stats.funnelData && stats.funnelData.length > 0 && (
                    <Card className="col-span-full">
                        <CardHeader>
                            <CardTitle>Funnel Analysis</CardTitle>
                            <CardDescription>
                                User progression through onboarding screens
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-end justify-around gap-4 h-64 mb-8">
                                {stats.funnelData.map((step, index) => {
                                    const maxViews = stats.funnelData[0].views;
                                    const heightPercent = (step.views / maxViews) * 100;
                                    const conversionRate = index === 0 ? 100 : step.retention;

                                    return (
                                        <div key={step.index} className="flex-1 flex flex-col items-center gap-2">
                                            {/* Bar */}
                                            <div className="relative w-full flex items-end justify-center" style={{ height: '200px' }}>
                                                <div
                                                    className="w-full max-w-[80px] bg-gradient-to-t from-blue-600 to-blue-400 rounded-t-lg transition-all duration-500 hover:opacity-80"
                                                    style={{ height: `${heightPercent}%` }}
                                                />
                                            </div>

                                            {/* Step info */}
                                            <div className="text-center space-y-1">
                                                <div className="flex items-center justify-center gap-1">
                                                    <div className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-bold">
                                                        {index + 1}
                                                    </div>
                                                    <span className="font-medium text-sm truncate max-w-[120px]">{step.name}</span>
                                                </div>
                                                <div className="flex items-center justify-center gap-1 text-xs">
                                                    <span className="text-green-600 font-semibold">→</span>
                                                    <span className="font-bold">{step.views.toLocaleString()} users</span>
                                                    <span className="text-muted-foreground">({conversionRate}%)</span>
                                                </div>
                                                {index > 0 && step.dropOff > 0 && (
                                                    <div className="flex items-center justify-center gap-1 text-xs text-red-500">
                                                        <span>↘</span>
                                                        <span className="font-semibold">{stats.funnelData[index - 1].views - step.views} users</span>
                                                        <span>({step.dropOff}%)</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                )}

                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
                    <Card className="col-span-4">
                        <CardHeader>
                            <CardTitle>Overview</CardTitle>
                            <CardDescription>
                                Daily views and completions
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="pl-2">
                            <div className="h-[350px] w-full">
                                {stats.chartData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={stats.chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                            <defs>
                                                <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                                                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                                                </linearGradient>
                                                <linearGradient id="colorCompletions" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                                                    <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <XAxis dataKey="date" stroke="#888888" fontSize={12} tickLine={false} axisLine={false} />
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                                            <Tooltip
                                                contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                                itemStyle={{ fontSize: '12px' }}
                                            />
                                            <Area type="monotone" dataKey="views" stroke="#8884d8" fillOpacity={1} fill="url(#colorViews)" strokeWidth={2} />
                                            <Area type="monotone" dataKey="completions" stroke="#82ca9d" fillOpacity={1} fill="url(#colorCompletions)" strokeWidth={2} />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="flex items-center justify-center h-full text-muted-foreground">
                                        No data available yet
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="col-span-3">
                        <CardHeader>
                            <CardTitle>Activity Log</CardTitle>
                            <CardDescription>
                                Real-time user interactions and events
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {loading ? (
                                <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
                                    Loading activity...
                                </div>
                            ) : (
                                <ActivityLog project={project} supabase={supabase} />
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

// Activity Log Component
function ActivityLog({ project, supabase }) {
    const [events, setEvents] = React.useState([]);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        fetchEvents();

        // Set up real-time subscription
        const subscription = supabase
            .channel(`analytics:${project.id}`)
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'public',
                table: 'analytics_events',
                filter: `project_id=eq.${project.id}`
            }, (payload) => {
                setEvents(prev => [payload.new, ...prev].slice(0, 50)); // Keep last 50 events
            })
            .subscribe();

        return () => {
            subscription.unsubscribe();
        };
    }, [project.id]);

    const fetchEvents = async () => {
        try {
            const { data, error } = await supabase
                .from('analytics_events')
                .select('*')
                .eq('project_id', project.id)
                .order('created_at', { ascending: false })
                .limit(50);

            if (error) throw error;
            setEvents(data || []);
        } catch (error) {
            console.error('Error fetching events:', error);
        } finally {
            setLoading(false);
        }
    };

    const getEventIcon = (eventType) => {
        switch (eventType) {
            case 'view':
                return <Users className="h-4 w-4 text-blue-500" />;
            case 'interaction':
                return <MousePointerClick className="h-4 w-4 text-purple-500" />;
            case 'input':
                return <ArrowUpRight className="h-4 w-4 text-green-500" />;
            case 'completion':
                return <Clock className="h-4 w-4 text-orange-500" />;
            default:
                return <Users className="h-4 w-4 text-gray-500" />;
        }
    };

    const getEventColor = (eventType) => {
        switch (eventType) {
            case 'view':
                return 'bg-blue-50 border-blue-200';
            case 'interaction':
                return 'bg-purple-50 border-purple-200';
            case 'input':
                return 'bg-green-50 border-green-200';
            case 'completion':
                return 'bg-orange-50 border-orange-200';
            default:
                return 'bg-gray-50 border-gray-200';
        }
    };

    const formatEventData = (event) => {
        const metadata = event.properties || {};

        switch (event.event_type) {
            case 'view':
                return `Viewed ${metadata.screen_name || `Screen ${metadata.screen_index + 1}`}`;
            case 'interaction':
                return `${metadata.action || 'Clicked'} button`;
            case 'input':
                return `Entered ${metadata.variable_name}`;
            case 'completion':
                const timeSpent = metadata.time_spent_ms ? `(${Math.round(metadata.time_spent_ms / 1000)}s)` : '';
                return `Completed onboarding ${timeSpent}`;
            default:
                return event.event_type;
        }
    };

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
        return date.toLocaleDateString();
    };

    if (loading) {
        return <div className="text-center text-muted-foreground py-8">Loading events...</div>;
    }

    if (events.length === 0) {
        return (
            <div className="text-center text-muted-foreground py-12">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p>No activity yet</p>
                <p className="text-xs mt-1">Events will appear here as users interact with your onboarding</p>
            </div>
        );
    }

    return (
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {events.map((event, index) => (
                <div
                    key={event.id || index}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${getEventColor(event.event_type)} transition-all hover:shadow-sm`}
                >
                    <div className="mt-0.5">
                        {getEventIcon(event.event_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-medium text-gray-900">
                                {formatEventData(event)}
                            </p>
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                                {formatTime(event.created_at)}
                            </span>
                        </div>
                        {event.session_id && (
                            <p className="text-xs text-gray-500 mt-1 font-mono truncate">
                                Session: {event.session_id.slice(0, 16)}...
                            </p>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default Analytics;
